/**
 * Loans self-service panel (M3X.LOANS PR4 — F.2).
 *
 * Rendered inside the Payment Hub's Loan Request tab. Shows:
 *   - Article 90 headroom widget (Mutaba'aa-attributed) — the cap snapshot
 *   - Active loan balance cards: principal, outstanding, monthly,
 *     progress bar with % paid
 *   - Loan history table with filters
 *   - "Request a Loan" CTA → links to /me/loans/new (the wizard lands in PR5)
 *
 * Reads the calling user's employee_id from URL query for v1 (HR can
 * impersonate by passing ?employee_id=X). A future PR resolves it from
 * Clerk metadata.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Article90HeadroomWidget } from "../components/Article90HeadroomWidget";
import { api, type LoanApplicationDetail } from "../lib/api";
import { ErrorPanel, Loading } from "./Loading";

export function LoansSelfServicePanel() {
  const [search] = useSearchParams();
  const employeeId = search.get("employee_id") ?? "";

  if (!employeeId) {
    return <NoEmployeeIdPanel />;
  }

  return <Body employeeId={employeeId} />;
}

function NoEmployeeIdPanel() {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
      <p className="text-sm text-amber-900">
        {t(
          "loans_self.missing_employee_id",
          "Sign in to view your loan balance. HR can view any employee's loans from the Employees page.",
        )}
      </p>
    </div>
  );
}

function Body({ employeeId }: { employeeId: string }) {
  const { t } = useTranslation();

  const apps = useQuery({
    queryKey: ["my-loans", employeeId],
    queryFn: () => api.listLoanApplications({ employeeId }),
  });

  const activeLoans = useMemo(
    () =>
      (apps.data ?? []).filter(
        (a) => a.workflow_state === "in_repayment" || a.workflow_state === "disbursed",
      ),
    [apps.data],
  );

  return (
    <div className="space-y-6">
      <Article90HeadroomWidget employeeId={employeeId} />

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-text-primary">
            {t("loans_self.active_loans_title", "Active loans")}
          </h3>
          <Link
            to={`/me/loans/new?employee_id=${encodeURIComponent(employeeId)}`}
            className="rounded-md bg-brand-blue text-white px-3 py-1.5 text-sm font-medium hover:bg-brand-deep"
          >
            {t("loans_self.request_loan_cta", "Request a Loan")}
          </Link>
        </div>
        {apps.isLoading ? <Loading /> : null}
        {apps.error ? (
          <ErrorPanel
            message={t(
              "loans_self.load_error",
              "Couldn't load your loans.",
            )}
          />
        ) : null}
        {!apps.isLoading && activeLoans.length === 0 ? (
          <EmptyActive />
        ) : null}
        {activeLoans.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {activeLoans.map((loan) => (
              <LoanBalanceCard key={loan.id} loan={loan} />
            ))}
          </div>
        ) : null}
      </div>

      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-3">
          {t("loans_self.history_title", "Loan history")}
        </h3>
        {(apps.data ?? []).length === 0 ? (
          <div className="rounded-md border border-slate-200 bg-white p-6 text-center text-sm text-text-tertiary">
            {t("loans_self.no_history", "No loans on record yet.")}
          </div>
        ) : (
          <LoanHistoryTable items={apps.data ?? []} />
        )}
      </div>
    </div>
  );
}

function LoanBalanceCard({ loan }: { loan: LoanApplicationDetail }) {
  const { t } = useTranslation();
  const approved = Number.parseFloat(loan.approved_amount ?? "0");
  const requested = Number.parseFloat(loan.requested_amount ?? "0");
  const principal = approved > 0 ? approved : requested;
  // Repaid % is computed client-side from approved amount × elapsed
  // installments — but the wire data shape from listLoanApplications
  // doesn't include current outstanding. Show approved + monthly only
  // until the schedule endpoint is queried (deferred to LoanDetailPage
  // §4.7 in PR24).
  const monthly = Number.parseFloat(
    loan.monthly_installment ?? "0",
  );
  return (
    <Link
      to={`/operations/loans/applications/${loan.id}`}
      className="block rounded-lg border border-slate-200 bg-surface-default p-4 hover:border-brand-blue transition-colors"
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-xs text-text-tertiary uppercase">
            {loan.loan_product_id.slice(0, 8)}
          </div>
          <div className="text-lg font-semibold text-text-primary">
            {principal.toLocaleString()} SAR
          </div>
        </div>
        <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800">
          {loan.workflow_state}
        </span>
      </div>
      <div className="text-sm text-text-secondary">
        {t("loans_self.monthly", "Monthly:")}{" "}
        <span className="font-mono">{monthly.toLocaleString()} SAR</span>
      </div>
    </Link>
  );
}

function EmptyActive() {
  const { t } = useTranslation();
  return (
    <div className="rounded-md border border-slate-200 bg-white p-6 text-center text-sm text-text-tertiary">
      {t(
        "loans_self.no_active_loans",
        "You don't have any active loans right now. Click 'Request a Loan' to start one.",
      )}
    </div>
  );
}

function LoanHistoryTable({ items }: { items: LoanApplicationDetail[] }) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50">
          <tr>
            <th className="text-start p-2 font-medium text-text-tertiary">
              {t("loans_self.col_type", "Type")}
            </th>
            <th className="text-start p-2 font-medium text-text-tertiary">
              {t("loans_self.col_amount", "Amount")}
            </th>
            <th className="text-start p-2 font-medium text-text-tertiary">
              {t("loans_self.col_state", "State")}
            </th>
            <th className="text-start p-2 font-medium text-text-tertiary">
              {t("loans_self.col_submitted", "Submitted")}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((loan) => (
            <tr
              key={loan.id}
              className="border-t border-slate-200 hover:bg-slate-50"
            >
              <td className="p-2">{loan.loan_product_id.slice(0, 8)}</td>
              <td className="p-2 font-mono">
                {Number.parseFloat(
                  loan.requested_amount ?? "0",
                ).toLocaleString()}{" "}
                SAR
              </td>
              <td className="p-2">
                <span className="text-xs px-2 py-0.5 rounded bg-slate-100">
                  {loan.workflow_state}
                </span>
              </td>
              <td className="p-2 text-text-tertiary">
                {loan.submitted_at?.slice(0, 10) ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
